import { useCallback, useEffect, useState } from 'react';
import { api, apiRaw, describeError } from '../api';
import { CountedField, FileButton, JsonEditor, Message, NAME_MAX, Pill, Section, fmtTime, useJsonText } from '../components/ui';
import { useSession } from '../session';

interface Persona { id: string; name: string; avatar_url: string | null; description: Record<string, unknown>; is_default: boolean; updated_at: string; deleted_at?: string | null }
const POSITIONS: Array<[number, string]> = [[0, 'IN_PROMPT'], [2, 'TOP_AN'], [3, 'BOTTOM_AN'], [4, 'AT_DEPTH'], [9, 'NONE']];
type Gender = 'male' | 'female' | 'other' | '';
const EMPTY_FORM = { name: '', description: '', position: 0, depth: 2, gender: '' as Gender, example: '', useAccountAvatar: true };

/** add-mvp-protocol-gaps-3: copies the account avatar into an independent persona object (`POST /api/media/copy`). */
async function copyAccountAvatar(): Promise<string | null> {
  const me = (await api<{ avatar_key: string | null }>('/api/me')).data;
  if (!me.avatar_key) return null;
  const { data } = await api<{ key: string }>('/api/media/copy', { method: 'POST', body: { source_key: me.avatar_key, purpose: 'persona' } });
  return data.key;
}

export function PersonasPage() {
  const { env, me } = useSession();
  const [items, setItems] = useState<Persona[]>([]);
  const [selected, setSelected] = useState<Persona | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const editor = useJsonText({});
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;
    try { setItems((await api<Persona[]>(`/api/personas${includeDeleted ? '?include_deleted=1' : ''}`)).data); } catch (e) { setErr(describeError(e)); }
  }, [me, includeDeleted]);
  useEffect(() => { void load(); }, [load, env]);

  const run = async (fn: () => Promise<string | void>) => {
    setErr(null); setMsg(null);
    try { const m = await fn(); if (m) setMsg(m); await load(); } catch (e) { setErr(describeError(e)); }
  };
  const create = () => run(async () => {
    const pockettavern: Record<string, unknown> = { avatar_text: form.name.trim().slice(0, 1) };
    if (!form.useAccountAvatar) pockettavern.avatar_source = 'letter';
    if (form.gender) pockettavern.gender = form.gender;
    if (form.example.trim()) pockettavern.example = form.example.trim();
    const { data } = await api<Persona>('/api/personas', { method: 'POST', body: { name: form.name, ...(form.useAccountAvatar ? {} : { avatar_key: null }), description: { description: form.description, position: form.position, depth: form.depth, pockettavern } } });
    setForm(EMPTY_FORM);
    setSelected(data); editor.reset(data.description);
    return `已创建 ${data.name}${data.avatar_url ? '（账号头像已复制为独立对象）' : form.useAccountAvatar ? '（账号无头像，用首字色块）' : ''}`;
  });
  const useAccountAvatarNow = () => run(async () => {
    if (!selected) return;
    const key = await copyAccountAvatar();
    if (!key) return '账号没有头像，无法复制';
    const { data } = await api<Persona>(`/api/personas/${selected.id}`, { method: 'PATCH', body: { avatar_key: key, description: { ...selected.description, pockettavern: { ...((selected.description.pockettavern as Record<string, unknown> | undefined) ?? {}), avatar_source: 'image' } } } });
    setSelected(data); editor.reset(data.description);
    return `已复制账号头像 → ${key}（之后互不影响）`;
  });
  const select = (p: Persona) => { setSelected(p); editor.reset(p.description); };
  const saveDescription = () => { if (!selected) return; const v = editor.parse(); if (v === undefined) return; void run(async () => { const { data } = await api<Persona>(`/api/personas/${selected.id}`, { method: 'PATCH', body: { description: v } }); setSelected(data); return '已保存 description'; }); };
  const uploadAvatar = (file: File) => run(async () => {
    if (!selected) return;
    const up = await apiRaw('/api/media/upload?purpose=persona', { method: 'POST', body: file, headers: { 'Content-Type': file.type || 'image/png' } });
    const body = (await up.json()) as { success: boolean; data: { key: string }; error?: { code: string; message: string } };
    if (!body.success) throw new Error(`${up.status} ${body.error?.code} — ${body.error?.message}`);
    const { data } = await api<Persona>(`/api/personas/${selected.id}`, { method: 'PATCH', body: { avatar_key: body.data.key } });
    setSelected(data);
    return `头像已绑定 ${body.data.key}`;
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={`人设 · ${items.length}`}>
        {!me && <Message text="请先登录" />}
        <div className="grid gap-2 sm:grid-cols-2">
          <CountedField limit={NAME_MAX} placeholder="名称" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <select className="input" value={form.position} onChange={(e) => setForm({ ...form, position: Number(e.target.value) })}>{POSITIONS.map(([v, l]) => <option key={v} value={v}>{v} · {l}</option>)}</select>
          <CountedField multiline className="input sm:col-span-2" rows={2} placeholder="description（{{user}} 的人设描述）" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          <select className="input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value as Gender })} title="description.pockettavern.gender">
            <option value="">性别（不填）</option><option value="male">male</option><option value="female">female</option><option value="other">other</option>
          </select>
          <input className="input" placeholder="首句 example（仅展示，不进提示词）" value={form.example} onChange={(e) => setForm({ ...form, example: e.target.value })} title="description.pockettavern.example" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button className="btn" disabled={!form.name.trim()} onClick={() => void create()}>创建</button>
          <label className="flex items-center gap-1 text-xs text-zinc-400" title="省略 avatar_key：服务端把账号头像复制为独立 persona 对象；之后改账号头像不影响人设"><input type="checkbox" checked={form.useAccountAvatar} onChange={(e) => setForm({ ...form, useAccountAvatar: e.target.checked })} /> 默认使用账号头像（复制一份）</label>
          <label className="flex items-center gap-1 text-xs text-zinc-400"><input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} /> 含已删除</label>
        </div>
        <Message text={err} kind="error" />
        <Message text={msg} kind="ok" />
        <ul className="divide-y divide-zinc-800">
          {items.map((p) => (
            <li key={p.id} className={`flex cursor-pointer items-center gap-3 py-2 ${selected?.id === p.id ? 'bg-zinc-800/40' : ''}`} onClick={() => select(p)}>
              {p.avatar_url ? <img src={p.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-zinc-800" />}
              <div className="flex-1 text-sm">
                {p.name} {p.is_default && <Pill tone="emerald">默认</Pill>} {p.deleted_at && <Pill tone="rose">已删除</Pill>}
                {(() => { const ext = p.description.pockettavern as { gender?: string; example?: string; avatar_source?: string } | undefined; return ext ? <span className="ml-1 text-[11px] text-zinc-500">{[ext.gender, ext.avatar_source, ext.example ? `“${ext.example.slice(0, 24)}”` : null].filter(Boolean).join(' · ')}</span> : null; })()}
                <div className="text-[11px] text-zinc-500">{fmtTime(p.updated_at)}</div>
              </div>
              {!p.is_default && !p.deleted_at && <button className="btn-ghost py-0.5 text-[11px]" onClick={(e) => { e.stopPropagation(); void run(async () => { await api(`/api/personas/${p.id}/default`, { method: 'PUT' }); return `${p.name} 设为默认`; }); }}>设为默认</button>}
            </li>
          ))}
        </ul>
      </Section>
      <Section
        title={selected ? selected.name : 'PersonaDescription'}
        actions={selected && (
          <>
            <FileButton label="上传头像" accept="image/*" onFile={uploadAvatar} />
            {!selected.deleted_at && <button className="btn-ghost" title="POST /api/media/copy（账号头像 → 独立的 persona 对象）" onClick={() => void useAccountAvatarNow()}>使用账号头像</button>}
            <button className="btn" onClick={saveDescription}>保存 description</button>
            {!selected.deleted_at && <button className="btn-ghost" title="POST /api/personas/{id}/duplicate" onClick={() => void run(async () => { const { data } = await api<{ name: string }>(`/api/personas/${selected.id}/duplicate`, { method: 'POST' }); return `已复制 → ${data.name}`; })}>复制</button>}
            {selected.deleted_at ? <button className="btn-ghost" onClick={() => void run(async () => { await api(`/api/personas/${selected.id}/restore`, { method: 'POST' }); return '已恢复'; })}>恢复</button>
              : <button className="btn-ghost text-rose-300" onClick={() => void run(async () => { await api(`/api/personas/${selected.id}`, { method: 'DELETE' }); setSelected(null); return '已删除'; })}>删除</button>}
          </>
        )}
      >
        {selected ? <JsonEditor value={editor.text} onChange={editor.setText} error={editor.error} rows={14} /> : <div className="text-xs text-zinc-500">选择一个人设；position 只接受 0/1/2/3/9。`description.pockettavern {'{'} gender · example · avatar_source(image|letter) · avatar_color · avatar_text {'}'}` 为服务端类型化的展示扩展（add-mvp-protocol-gaps-3）；非法值 400。</div>}
      </Section>
    </div>
  );
}
